import React from 'react';
import { Button, Col, Drawer, Row, Table, Tag } from 'antd';
import { useState, useEffect } from 'react';
import PageTitle from 'shared_components/page_title';
import DownloadCSV from 'shared_components/download_csv';
import { fetchSpecimensItems, applySorting } from 'services/settings/lookup_table/specimens';
import SpecimensForm from './specimensForm';
import { DndProvider } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import update from 'immutability-helper';
import DragableBodyRow from 'shared_components/drag_and_drop_row';
import { showMessage } from 'services/settings/message';

function Specimens() {
	const [specimens, setSpecimens] = useState([]);
	const [hasReordered, setHasReordered] = useState(false);
	const [drawerState, setDrawerState] = useState({
		visible: false,
		drawerTitle: '',
		selecetedData: null,
		buttonNames: '',
	});

	useEffect(() => {
		getSpecimens();
	}, []);

	const getSpecimens = async () => {
		const data = await fetchSpecimensItems();
		const records = data.RECORDS || data || [];
		// Sort by specimenSort ASC, but put records without specimenSort at the end
		const sortedData = records.sort((a, b) => {
			if (a.specimenSort != null && b.specimenSort != null) {
				return a.specimenSort - b.specimenSort;
			}
			if (a.specimenSort == null) return 1;
			if (b.specimenSort == null) return -1;
			return 0;
		});
		setSpecimens(sortedData);
	};

	const onSuccessCreate = async () => {
		onDrawerClose();
		await getSpecimens();
		showMessage('M0002');
	};

	const onSuccessUpdate = async () => {
		onDrawerClose();
		await getSpecimens();
		showMessage('M0003');
	};

	const applySort = async () => {
		const dataPayload = specimens.map((rec, index) => {
			return {
				specimenID: rec.specimenID,
				specimenSort: index + 1,
			};
		});

		await applySorting(dataPayload);
		setHasReordered(false);
	};

	const moveRow = (dragIndex, hoverIndex) => {
		const dragRow = specimens[dragIndex];

		const updatedData = update({ specimens }, {
			specimens: { $splice: [[dragIndex, 1], [hoverIndex, 0, dragRow]] }
		});

		setSpecimens(updatedData.specimens);
		setHasReordered(true);
	};

	const showDrawer = record => {
		setDrawerState({
			visible: true,
			drawerTitle: 'ADD SPECIMEN',
			selecetedData: null,
			buttonNames: 'ADD',
		});
	};

	const displayDrawer = record => {
		setDrawerState({
			visible: true,
			drawerTitle: 'UPDATE SPECIMENS',
			selecetedData: record,
			buttonNames: 'UPDATE',
		});
	};

	const onDrawerClose = () => {
		setDrawerState({
			visible: false,
			drawerTitle: '',
			selecetedData: null,
			buttonNames: '',
		});
	};

	const components = {
		body: {
			row: DragableBodyRow,
		},
	};

	const columns = [
		{
			title: 'SPECIMEN NAME',
			dataIndex: 'specimenName',
			width: 400,
			sorter: (a, b) => { return a.specimenName.localeCompare(b.specimenName) },
			sortDirections: ['ascend', 'descend'],
		},
		{
			title: 'DATE CREATED',
			dataIndex: 'createdDate',
			width: 200,
			render: date => date ? new Date(date).toLocaleDateString() : '',
		},
		{
			title: 'STATUS',
			dataIndex: 'isActive',
			width: 100,
			render: active => (
				<Tag color={active === 0 ? 'red' : 'green'}>{active === 0 ? 'INACTIVE' : 'ACTIVE'}</Tag>
			),
		},
	];

	return (
		<>
			<Row justify="end" style={{ marginTop: 12, marginBottom: 12, display: 'flex', gap: '10px' }}>
				<DownloadCSV
					confirmRequired={true}
					isLookupTable={true}
					confirmDetails={{
						section: 'SPECIMENS',
						count: specimens.length
					}}
					data={specimens}
					filename="specimens"
					columns={[
						{ title: 'SPECIMEN NAME', dataIndex: 'specimenName' },
						{ 
							title: 'DATE CREATED', 
							dataIndex: 'createdDate',
							render: date => date ? new Date(date).toLocaleDateString() : ''
						},
						{ 
							title: 'STATUS', 
							dataIndex: 'isActive',
							render: active => active === 0 ? 'INACTIVE' : 'ACTIVE'
						}
					]}
				/>
				<Button
					onClick={applySort}
					shape="round"
					type="primary"
					disabled={!hasReordered}
				>
					APPLY SORTING
				</Button>
				<Button
					type="primary"
					shape="round"
					onClick={showDrawer}
					style={{ marginRight: '15px' }}
				>
					ADD SPECIMEN
				</Button>
			</Row>
			<DndProvider backend={HTML5Backend}>
				<Table
					dataSource={specimens}
					columns={columns}
					components={components}
					pagination={false}
					rowKey={record => record.specimenID}
					onRow={(record, index) => ({
						index: specimens.findIndex(item => item.specimenID === record.specimenID),
						moveRow: moveRow,
						onDoubleClick: () => {
							displayDrawer(record);
						},
					})}
				/>
			</DndProvider>
			<Drawer
				title={drawerState.drawerTitle}
				width="30%"
				visible={drawerState.visible}
				onClose={onDrawerClose}
				destroyOnClose
			>
				<SpecimensForm
					buttonNames={drawerState.buttonNames}
					onDrawerClose={onDrawerClose}
					selecetedData={drawerState.selecetedData}
					onSuccessCreate={onSuccessCreate}
					onSuccessUpdate={onSuccessUpdate}
					maxSpecimenSort={Math.max(...specimens.map(s => s.specimenSort || 0), 0)}
				/>
			</Drawer>
		</>
	);
}

export default Specimens;
